/**
 * Host seam for selecting a studio tool from OUTSIDE the StudioApp React
 * subtree — e.g. the okrapdf.com ⌘K palette, which is mounted once at the
 * app root and cannot reach the workspace context. StudioApp registers its
 * selection handler while mounted; hosts call selectStudioTool() and fall
 * back to their own routing when it returns false (no studio mounted, or the
 * tool unknown / hidden on this host).
 */

export type StudioToolSelectHandler = (toolId: string) => boolean;

let activeHandler: StudioToolSelectHandler | null = null;

/** Register the mounted studio's selection handler; returns an unregister fn. */
export function registerStudioToolSelectHandler(handler: StudioToolSelectHandler): () => void {
  return Effect.runSync(registerStudioToolSelectHandlerEffect(handler));
}

export function registerStudioToolSelectHandlerEffect(
  handler: StudioToolSelectHandler,
): Effect.Effect<() => void> {
  return Effect.sync(() => {
    activeHandler = handler;
    return () => {
      if (activeHandler === handler) activeHandler = null;
    };
  });
}

/**
 * Ask the mounted studio to select a tool. Returns false when no studio is
 * mounted or the id did not resolve, so the caller can fall back to routing.
 */
export function selectStudioTool(toolId: string): boolean {
  return Effect.runSync(selectStudioToolEffect(toolId));
}

export function selectStudioToolEffect(toolId: string): Effect.Effect<boolean> {
  return Effect.sync(() => activeHandler?.(toolId) ?? false);
}
import { Effect } from 'effect';
